import type { AnalyticsEventName, AnalyticsEventProps, AnalyticsEventPayload } from './analyticsEvents'
import {
  createAnalyticsEventId,
  getAnalyticsAttribution,
  getAnalyticsDeviceId,
  getAnalyticsSessionId,
} from './analyticsSession'
import { enqueueAnalyticsEvent } from './analyticsQueue'
import { reportToYm } from './ymAdapter'
import { getPlatform } from '@shared/lib/platform/capacitorDetector'
import { useVersion } from '@shared/config/version'

const APP_VERSION = useVersion().version

/** Данные, которые знают только доменные слои. shared их не импортирует — их приносят сюда. */
export interface AnalyticsContext {
  userId?: number
  institutionId?: number
  branchId?: number
}

/**
 * Мост к доменным сторам, как `ApiTokenBridge` у API-клиента: shared/lib не имеет права
 * импортировать entities/features, поэтому userId и филиал приносит плагин слоя приложения.
 */
let contextProvider: (() => AnalyticsContext) | null = null

export const registerAnalyticsContextProvider = (provider: () => AnalyticsContext): void => {
  contextProvider = provider
}

// Отпечаток устройства живёт в analyticsSession: он нужен и здесь, и очереди отправки
// (доклеить к событиям, уехавшим в буфер до того, как отпечаток досчитался).
export { setAnalyticsDeviceId } from './analyticsSession'

const resolvePlatform = (): string => {
  try {
    return getPlatform()
  } catch {
    return 'web'
  }
}

const resolveContext = (): AnalyticsContext => {
  try {
    return contextProvider?.() ?? {}
  } catch {
    // Стор ещё не поднят или упал — событие уедет без привязки, но уедет.
    return {}
  }
}

/**
 * Единственная точка отправки продуктовых событий.
 *
 * @example
 * const { track } = useAnalytics()
 * track('add_to_cart', { productId: 42, quantity: 1, total: 590, source: 'catalog' })
 */
export const useAnalytics = () => {
  const track = <N extends AnalyticsEventName>(name: N, props: AnalyticsEventProps<N>): void => {
    if (!process.client) return

    const context = resolveContext()
    const attribution = getAnalyticsAttribution()

    const payload: AnalyticsEventPayload = {
      eventId: createAnalyticsEventId(),
      eventName: name,
      eventTime: new Date().toISOString(),
      sessionId: getAnalyticsSessionId(),
      deviceId: getAnalyticsDeviceId(),
      userId: context.userId,
      institutionId: context.institutionId,
      branchId: context.branchId,
      platform: resolvePlatform(),
      appVersion: APP_VERSION,
      source: attribution?.source,
      props: {
        ...(props as Record<string, unknown>),
        // Пуш/deeplink прилипает ко всем событиям сессии: так видно выручку с рассылки,
        // а не только её открываемость.
        ...(attribution?.notificationId ? { attributionNotificationId: attribution.notificationId } : {}),
        ...(attribution?.campaign ? { attributionCampaign: attribution.campaign } : {}),
        // Рекламные метки — тем же способом: разбивка воронки по кампании считается
        // из props, отдельных колонок под каждый utm заводить не пришлось.
        ...(attribution?.utmSource ? { utmSource: attribution.utmSource } : {}),
        ...(attribution?.utmMedium ? { utmMedium: attribution.utmMedium } : {}),
        ...(attribution?.utmCampaign ? { utmCampaign: attribution.utmCampaign } : {}),
        ...(attribution?.utmContent ? { utmContent: attribution.utmContent } : {}),
        ...(attribution?.utmTerm ? { utmTerm: attribution.utmTerm } : {}),
      },
    }

    enqueueAnalyticsEvent(payload)
    reportToYm(name, props)
  }

  return { track }
}
